import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../firebase/config";
import { COLLECTIONS, STORAGE_PATHS } from "../utils/constants";
import { cachedFetch, invalidateCache } from "./cacheService";

// Category Management
export const createCategory = async (categoryData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.CATEGORIES), {
      ...categoryData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await invalidateCache(COLLECTIONS.CATEGORIES);
    return { id: docRef.id, ...categoryData };
  } catch (error) {
    console.error("Error creating category:", error);
    throw new Error("Failed to create category");
  }
};

export const updateCategory = async (categoryId, categoryData) => {
  try {
    const categoryRef = doc(db, COLLECTIONS.CATEGORIES, categoryId);
    await updateDoc(categoryRef, {
      ...categoryData,
      updatedAt: serverTimestamp(),
    });
    await invalidateCache(COLLECTIONS.CATEGORIES);
    return { id: categoryId, ...categoryData };
  } catch (error) {
    console.error("Error updating category:", error);
    throw new Error("Failed to update category");
  }
};

export const deleteCategory = async (categoryId) => {
  try {
    // Check if category has items
    const itemsQuery = query(
      collection(db, COLLECTIONS.ITEMS),
      where("categoryId", "==", categoryId),
    );
    const itemsSnapshot = await getDocs(itemsQuery);

    if (!itemsSnapshot.empty) {
      throw new Error("Cannot delete category that contains items");
    }

    await deleteDoc(doc(db, COLLECTIONS.CATEGORIES, categoryId));
    await invalidateCache(COLLECTIONS.CATEGORIES);
    return categoryId;
  } catch (error) {
    console.error("Error deleting category:", error);
    throw error;
  }
};

export const getCategories = async () => {
  return cachedFetch(COLLECTIONS.CATEGORIES, async () => {
    const q = query(collection(db, COLLECTIONS.CATEGORIES), orderBy("name"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  });
};

export const getCategory = async (categoryId) => {
  try {
    const docRef = doc(db, COLLECTIONS.CATEGORIES, categoryId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      throw new Error("Category not found");
    }
  } catch (error) {
    console.error("Error fetching category:", error);
    throw error;
  }
};

// Item Management
export const createItem = async (itemData, imageFile = null) => {
  try {
    let imageUrl = null;

    // Upload image if provided
    if (imageFile) {
      const imageRef = ref(
        storage,
        `${STORAGE_PATHS.ITEM_IMAGES}/${Date.now()}_${imageFile.name}`,
      );
      const uploadResult = await uploadBytes(imageRef, imageFile);
      imageUrl = await getDownloadURL(uploadResult.ref);
    }

    const docRef = await addDoc(collection(db, COLLECTIONS.ITEMS), {
      ...itemData,
      imageUrl,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await invalidateCache(COLLECTIONS.ITEMS);
    return { id: docRef.id, ...itemData, imageUrl };
  } catch (error) {
    console.error("Error creating item:", error);
    throw new Error("Failed to create item");
  }
};

export const updateItem = async (itemId, itemData, imageFile = null) => {
  try {
    let imageUrl = itemData.imageUrl;

    // Upload new image if provided
    if (imageFile) {
      // Delete old image if exists
      if (itemData.imageUrl) {
        try {
          const oldImageRef = ref(storage, itemData.imageUrl);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.warn("Could not delete old image:", error);
        }
      }

      const imageRef = ref(
        storage,
        `${STORAGE_PATHS.ITEM_IMAGES}/${Date.now()}_${imageFile.name}`,
      );
      const uploadResult = await uploadBytes(imageRef, imageFile);
      imageUrl = await getDownloadURL(uploadResult.ref);
    }

    const itemRef = doc(db, COLLECTIONS.ITEMS, itemId);
    const updateData = {
      ...itemData,
      imageUrl,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(itemRef, updateData);
    await invalidateCache(COLLECTIONS.ITEMS);
    return { id: itemId, ...updateData };
  } catch (error) {
    console.error("Error updating item:", error);
    throw new Error("Failed to update item");
  }
};

export const deleteItem = async (itemId) => {
  try {
    // Get item to delete image
    const itemDoc = await getDoc(doc(db, COLLECTIONS.ITEMS, itemId));
    if (itemDoc.exists()) {
      const itemData = itemDoc.data();

      // Delete image if exists
      if (itemData.imageUrl) {
        try {
          const imageRef = ref(storage, itemData.imageUrl);
          await deleteObject(imageRef);
        } catch (error) {
          console.warn("Could not delete item image:", error);
        }
      }
    }

    await deleteDoc(doc(db, COLLECTIONS.ITEMS, itemId));
    await invalidateCache(COLLECTIONS.ITEMS);
    return itemId;
  } catch (error) {
    console.error("Error deleting item:", error);
    throw new Error("Failed to delete item");
  }
};

export const getItems = async (categoryId = null) => {
  // Filtered queries bypass cache since cache stores the full collection
  if (categoryId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ITEMS),
        where("categoryId", "==", categoryId),
        orderBy("name"),
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error("Error fetching items:", error);
      throw new Error("Failed to fetch items");
    }
  }

  return cachedFetch(COLLECTIONS.ITEMS, async () => {
    const q = query(collection(db, COLLECTIONS.ITEMS), orderBy("name"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  });
};

export const getItem = async (itemId) => {
  try {
    const docRef = doc(db, COLLECTIONS.ITEMS, itemId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      throw new Error("Item not found");
    }
  } catch (error) {
    console.error("Error fetching item:", error);
    throw error;
  }
};

export const searchItems = async (searchTerm) => {
  try {
    const q = query(collection(db, COLLECTIONS.ITEMS), orderBy("name"));
    const querySnapshot = await getDocs(q);

    const items = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Client-side filtering for search
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.barcode?.includes(searchTerm),
    );
  } catch (error) {
    console.error("Error searching items:", error);
    throw new Error("Failed to search items");
  }
};

export const updateItemStock = async (itemId, quantity) => {
  try {
    const itemRef = doc(db, COLLECTIONS.ITEMS, itemId);
    await updateDoc(itemRef, {
      stockQuantity: quantity,
      updatedAt: serverTimestamp(),
    });
    return { itemId, quantity };
  } catch (error) {
    console.error("Error updating item stock:", error);
    throw new Error("Failed to update item stock");
  }
};

// ─── City-based Item Pricing (append-only, latest by created_at) ─────────────

const PRICE_COLLECTION = "item_city_prices";

/**
 * Get all price records for a city, then return a map of item_id → latest price record.
 * Fetches all docs for the city and picks the latest by created_at client-side
 * (avoids composite index requirement).
 */
export const getCityPrices = async (cityId) => {
  try {
    const q = query(
      collection(db, PRICE_COLLECTION),
      where("city_id", "==", cityId),
    );
    const snap = await getDocs(q);

    // Group by item_id, keep only the latest (highest created_at)
    const map = {};
    snap.docs.forEach((d) => {
      const data = { id: d.id, ...d.data() };
      const existing = map[data.item_id];
      if (!existing) {
        map[data.item_id] = data;
      } else {
        // Compare timestamps — keep the newer one
        const existingTs = existing.created_at?.toMillis?.() ?? 0;
        const newTs = data.created_at?.toMillis?.() ?? 0;
        if (newTs > existingTs) {
          map[data.item_id] = data;
        }
      }
    });

    return map;
  } catch (error) {
    console.error("Error fetching city prices:", error);
    throw new Error("Failed to fetch city prices");
  }
};

/**
 * Always creates a new price document (append-only history).
 * The latest document by created_at is the effective price.
 */
export const setItemPrice = async (
  itemId,
  cityId,
  price,
  unit = "piece",
  stockQuantity = 0,
) => {
  try {
    const docRef = await addDoc(collection(db, PRICE_COLLECTION), {
      item_id: itemId,
      city_id: cityId,
      price: Number(price),
      unit,
      stock_quantity: Number(stockQuantity),
      created_at: serverTimestamp(),
    });
    return {
      id: docRef.id,
      item_id: itemId,
      city_id: cityId,
      price: Number(price),
      unit,
      stock_quantity: Number(stockQuantity),
    };
  } catch (error) {
    console.error("Error setting item price:", error);
    throw new Error("Failed to set item price");
  }
};

/**
 * Get all price history records for a specific item in a specific city,
 * sorted by created_at ascending (oldest first, latest last).
 */
export const getItemPriceHistory = async (itemId, cityId) => {
  try {
    const q = query(
      collection(db, PRICE_COLLECTION),
      where("item_id", "==", itemId),
      where("city_id", "==", cityId),
    );
    const snap = await getDocs(q);
    const records = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    // Sort ascending by created_at (oldest first)
    records.sort((a, b) => {
      const aTs = a.created_at?.toMillis?.() ?? 0;
      const bTs = b.created_at?.toMillis?.() ?? 0;
      return aTs - bTs;
    });
    return records;
  } catch (error) {
    console.error("Error fetching item price history:", error);
    throw new Error("Failed to fetch price history");
  }
};
